import { RouteLocationMatched, RouteLocationNormalizedLoaded, useRouter } from 'vue-router';
import { GroupModel, NamespaceModel, QueueTasksModel } from '../models';
import { TreeItemType } from '../enums';
import { computed, ref } from 'vue';

export const useSidebarTree = () => {
  const router = useRouter();

  /**Элементы сайдбара */
  const sidebarItems = ref(new Array<NamespaceModel>());
  /**Устноваить элементы сайдбара */
  const setSidebarItems = (items: Array<NamespaceModel>) => {
    sidebarItems.value = items;
  };

  /**
   * * Пройтись по дереву
   * @param tree Элементы дерева
   * @param callback Коллбек
   * @returns
   */
  const foreachTree = (
    tree: Array<NamespaceModel | GroupModel>,
    callback: (item: NamespaceModel | GroupModel | QueueTasksModel) => boolean | undefined | void,
  ) => {
    for (let index = 0; index < tree.length; index++) {
      const item = tree[index];
      // Отмена прохождения по дереву
      const isCancel = callback(item);
      if (isCancel) return;
      if (item.isNamespace() || item.isGroup()) {
        if (item.Groups?.length) foreachTree(item.Groups, callback);
        if (item.QueueTasks?.length) {
          for (let j = 0; j < item.QueueTasks.length; j++) {
            const queue = item.QueueTasks[j];
            const isCancel = callback(queue);
            if (isCancel) return;
          }
        }
      }
    }
  };

  /**
   * * Отфильтрованное дерево
   * @param tree Дерево
   * @param predicate Функция для проверки условия
   * @returns
   */
  const filterTreeItems = <T extends Array<NamespaceModel> | Array<GroupModel>>(
    tree: T,
    predicate: (item: NamespaceModel | GroupModel | QueueTasksModel) => boolean,
  ): T => {
    const result: T = [] as T;

    tree.forEach((item: NamespaceModel | GroupModel) => {
      if (predicate(item)) {
        // Если элемент удовлетворяет условию, добавляем его в результат
        const newItem: any = { ...item };
        if (item.isNamespace() || item.isGroup()) {
          if (item.Groups) {
            newItem.Groups = filterTreeItems(item.Groups, predicate);
          }
          if (item.QueueTasks) {
            newItem.QueueTasks = item.QueueTasks.filter(predicate);
          }
        }
        result.push(newItem);
      }
    });

    return result;
  };

  /**
   * * Найти элемент в дереве
   * @param tree Элементы дерева
   * @param callback Коллбек
   * @returns
   */
  const findTreeItem = (
    tree: Array<NamespaceModel | GroupModel>,
    callback: (item: NamespaceModel | GroupModel | QueueTasksModel) => boolean,
  ): NamespaceModel | GroupModel | QueueTasksModel | undefined => {
    let find: NamespaceModel | GroupModel | QueueTasksModel | undefined;

    foreachTree(tree, (item) => {
      const isMatch = callback(item);
      if (isMatch) find = item;
      return isMatch;
    });

    return find;
  };

  /**
   * * Пройтись по родительским элементам дерева
   * @param tree Элементы дерева
   * @param item Дочерний элемент (от куда начинать)
   * @param callback Коллбек
   */
  const foreachParentItems = (
    tree: Array<NamespaceModel | GroupModel>,
    item: GroupModel | QueueTasksModel,
    callback: (item: NamespaceModel | GroupModel) => boolean | undefined | void,
  ) => {
    if (item.isQueue()) {
      const findGroup = findTreeItem(tree, (treeItem) => {
        if (!treeItem.isGroup()) return;
        return (
          treeItem.QueueTasks?.length && treeItem.QueueTasks.some((queue) => queue.Id === item.Id)
        );
      }) as GroupModel | undefined;
      if (findGroup) {
        const isCancel = callback(findGroup);
        if (isCancel) return;
        foreachParentItems(tree, findGroup, callback);
      } else if (item.NamespaceId !== undefined) {
        const findNamespace = tree.find(
          (treeItem) => treeItem.isNamespace() && item.NamespaceId === treeItem.Id,
        );

        if (findNamespace) callback(findNamespace);

        return;
      }
      return;
    }
    if (item.isGroup()) {
      if (item.ParentGroupId !== undefined) {
        const findGroup = findTreeItem(tree, (treeItem) => {
          if (!treeItem.isGroup()) return;
          return treeItem.Id === item.ParentGroupId;
        }) as GroupModel | undefined;
        if (findGroup) {
          const isCancel = callback(findGroup);
          if (isCancel) return;
          foreachParentItems(tree, findGroup, callback);
        }
        return;
      }

      if (item.NamespaceId !== undefined) {
        const findNamespace = tree.find(
          (treeItem) => treeItem.isNamespace() && item.NamespaceId === treeItem.Id,
        );

        if (findNamespace) callback(findNamespace);

        return;
      }
    }
  };

  /**
   * * Получить родительские элементы дерева относительно дочернего элемента
   * @param tree Элементы дерева
   * @param child Дочерний элемент
   * @returns
   */
  const getParents = (
    tree: Array<NamespaceModel | GroupModel>,
    child: GroupModel | QueueTasksModel,
  ) => {
    const parents = new Array<NamespaceModel | GroupModel>();

    foreachParentItems(tree, child, (item) => {
      parents.push(item);
    });

    return parents;
  };

  /**
   * * Получить родительский элемент относительно дочернего
   * @param tree Элементы дерева
   * @param child Дочерний элемент
   * @returns
   */
  const getParent = (
    tree: Array<NamespaceModel | GroupModel>,
    child: GroupModel | QueueTasksModel,
  ) => {
    let item: GroupModel | NamespaceModel;

    foreachParentItems(tree, child, (_item) => {
      item = _item;
      return true;
    });

    return item;
  };

  /**
   * * Получить тип элемента дерева на основании всех родительских роутов
   * @param matched Родительские роуты
   */
  const getTypeMenuItemFromRoute = (matched: RouteLocationMatched[]): TreeItemType | undefined => {
    const matchedItems = [...matched].reverse();
    for (let index = 0; index < matchedItems.length; index++) {
      const matchItem = matchedItems[index];
      switch (matchItem.name) {
        case 'namespace':
          return TreeItemType.Namespace;
        case 'group':
          return TreeItemType.Group;
        case 'queue':
          return TreeItemType.Queue;
      }
    }
  };

  /**
   * * Получить id элемента на основании роута
   * @param route Роут
   */
  const getIdFromRoute = (route: RouteLocationNormalizedLoaded): string | number | undefined => {
    const matchedItems = [...route.matched].reverse();
    const params = route.params;
    for (let index = 0; index < matchedItems.length; index++) {
      const matchItem = matchedItems[index];
      switch (matchItem.name) {
        case 'namespace':
          return params?.namespaceId as string | undefined;
        case 'group':
          const groupId = Number(params?.groupId);
          return isNaN(groupId) ? undefined : groupId;
        case 'queue':
          return params?.queueId as string | undefined;
      }
    }
  };

  /**
   * * Открыть элемент меню по роуту
   * @param route Роут
   * @returns
   */
  const openMenuItemFromRoute = (route: RouteLocationNormalizedLoaded) => {
    foreachTree(sidebarItems.value, (item) => {
      item.isSelectedInSidebar = false;
    });
    const menuItemType = getTypeMenuItemFromRoute(route.matched);
    if (menuItemType === undefined) return;
    const menuItemId = getIdFromRoute(route);
    if (
      menuItemId === undefined ||
      (menuItemType === TreeItemType.Group && typeof menuItemId !== 'number')
    )
      return;

    const findItem = findTreeItem(sidebarItems.value, (item) => {
      if (menuItemType !== item.Type) return;
      if (item.isNamespace() && typeof menuItemId === 'string' && item.Id === menuItemId)
        return true;
      if (item.isGroup() && typeof menuItemId === 'number' && item.Id === menuItemId) return true;
      if (item.isQueue() && typeof menuItemId === 'string' && item.Id === menuItemId) return true;

      return false;
    });

    if (!findItem) return;

    const parents = getParents(sidebarItems.value, findItem);

    document.title = findItem.Name.slice(0, 30);

    findItem.isSelectedInSidebar = true;
    parents.forEach((parentItem) => {
      parentItem.isOpenInSidebar = true;
    });
  };

  /**
   * * Добавить элемент меню
   * @param item Элемент меню
   * @returns
   */
  const addMenuItem = (
    item: NamespaceModel | GroupModel | QueueTasksModel,
    isOpen: boolean = false,
  ) => {
    // Добавление неймспейса
    if (item.isNamespace()) {
      sidebarItems.value.push(item);
      return;
    }

    const isNamespace = item.ParentType != TreeItemType.Group && !!item.NamespaceId;
    const parentId = isNamespace ? item.NamespaceId : item.ParentGroupId;

    // Добавление группы
    if (item.isGroup()) {
      if (parentId) {
        const findParentGroup = findTreeItem(sidebarItems.value, (_item) => {
          return (isNamespace ? _item.isNamespace() : _item.isGroup()) && parentId === _item.Id;
        }) as GroupModel | undefined;
        if (findParentGroup) {
          findParentGroup.Groups.push(item);
          sortByName(findParentGroup.Groups);
          if (isOpen) findParentGroup.isOpenInSidebar = true;
        }
      } else if (item.NamespaceId) {
        const findNamespace = sidebarItems.value.find(
          (namespace) => namespace.Id === item.NamespaceId,
        );
        if (findNamespace) {
          findNamespace.Groups.push(item);
          sortByName(findNamespace.Groups);
          if (isOpen) findNamespace.isOpenInSidebar = true;
        }
      }
      return;
    }
    // Добавление очереди
    console.log(item);
    if (item.isQueue() && parentId) {

      const findParentGroup = findTreeItem(sidebarItems.value, (_item) => {
        return (isNamespace ? _item.isNamespace() : _item.isGroup()) && parentId === _item.Id;
      }) as GroupModel | undefined;

      if (findParentGroup) {
        findParentGroup.QueueTasks.push(item);
        sortByName(findParentGroup.QueueTasks);
        if (isOpen) findParentGroup.isOpenInSidebar = true;
      }
      return;
    }
  };
  /**Сортировка по имени obj.Name */
  const sortByName = (items: any[]) => {
    return items.sort((a, b) => a?.Name.localeCompare(b?.Name));
  };
  /**
   * * Получает ближайший элемент массива на основании индекса.
   * @param array Массив элементов
   * @param index Индекс элемента отсносительно которого найти ближайший
   * @returns Ближайший элемент массива
   */
  const getNearestElement = <T>(array: T[], index: number): T => {
    // Если в массиве отсутвуют элементы
    if (array.length < 2) return;
    // Если индекс меньше нуля
    if (index - 1 < 0) return array[index + 1];
    // Если индекс равен или больше нуля
    else return array[index - 1];
  };

  /**
   * * Найти ближайший элемент в дереве
   * @param tree Дерево
   * @param start Стартовый элемент
   */
  const getNearestMenuItem = <T extends Array<NamespaceModel> | Array<GroupModel>>(
    tree: T,
    start: T extends Array<NamespaceModel>
      ? NamespaceModel | GroupModel | QueueTasksModel
      : GroupModel | QueueTasksModel,
  ) => {
    // Для неймспейса
    if (start.isNamespace()) {
      // Индекс неймспейса
      const namespaceIndex = sidebarItems.value.findIndex((namespace) => namespace.Id === start.Id);
      if (namespaceIndex === -1) return;
      // Cоседний неймспейс
      const nearestNamespace = getNearestElement(sidebarItems.value, namespaceIndex);

      return nearestNamespace;
    }

    return getParent(tree, start);
  };

  /**
   * * Редирект на ближайший элемент по дереву
   * @param tree Дерево
   * @param item Элемент относительно которого необходимо найти ближайший
   * @returns
   */
  const redirectToNearestMenuItem = (
    tree: Array<NamespaceModel>,
    item: NamespaceModel | GroupModel | QueueTasksModel,
  ) => {
    const nearestItem = getNearestMenuItem(tree, item);

    if (!nearestItem) {
      router.push({ name: 'project-namespaces' });
      return;
    }

    if (nearestItem.isNamespace()) {
      router.push({ name: 'namespace', params: { namespaceId: nearestItem.Id } });
      return;
    }
    if (nearestItem.isGroup()) {
      router.push({
        name: 'group',
        params: { groupId: nearestItem.Id, namespaceId: nearestItem.NamespaceId },
      });
      return;
    }
  };

  /**
   * * Удалить элемент меню
   * @param id ID
   * @param type Тип
   */
  const removeItem = <T extends string | number>(
    id: T,
    type: T extends number ? TreeItemType.Group : TreeItemType.Namespace | TreeItemType.Queue,
  ) => {
    const findItem = findTreeItem(
      sidebarItems.value,
      (item) => item.Type === type && item.Id === id,
    );
    if (!findItem) return;

    // Если элемент выбран в сайдбаре
    if (findItem.isSelectedInSidebar) redirectToNearestMenuItem(sidebarItems.value, findItem);

    if (type === TreeItemType.Namespace && typeof id === 'string') {
      sidebarItems.value = filterTreeItems(sidebarItems.value, (item) => {
        if (item.isNamespace()) return item.Id !== id;
        return true;
      });
    } else if (type === TreeItemType.Group && typeof id === 'number') {
      sidebarItems.value = filterTreeItems(sidebarItems.value, (item) => {
        if (item.isGroup()) return item.Id !== id;
        return true;
      });
    } else if (type === TreeItemType.Queue && typeof id === 'string') {
      sidebarItems.value = filterTreeItems(sidebarItems.value, (item) => {
        if (item.isQueue()) return item.Id !== id;
        return true;
      });
    }
  };

  /**
   * * Обновить элемент в дереве
   * @param item Элемент
   * @returns
   */
  const updateItem = (item: NamespaceModel | GroupModel | QueueTasksModel) => {
    const name = item.Name?.trim() || '';
    const color = (item as NamespaceModel).Color?.trim() || '';
    const icon = (item as NamespaceModel).IconUrl?.trim() || '';

    // Обновление неймспейса
    if (item.isNamespace()) {
      const findNamespace = sidebarItems.value.find((namespace) => namespace.Id === item.Id);

      if (findNamespace) Object.assign(findNamespace, { Name: name, Color: color, IconUrl: icon });

      sortByName(sidebarItems.value);
      return;
    }

    // Обновление группы
    if (item.isGroup()) {
      const findGroup = findTreeItem(sidebarItems.value, (_item) => {
        return _item.isGroup() && item.Id === _item.Id;
      }) as GroupModel | undefined;

      if (findGroup) Object.assign(findGroup, { ...findGroup, Name: name });

      if (findGroup.ParentGroupId) {
        sortSubItems(findGroup.ParentGroupId, TreeItemType.Group);
      } else if (findGroup.NamespaceId) {
        sortSubItems(findGroup.NamespaceId, TreeItemType.Namespace);
      }

      sortByName(sidebarItems.value);
      return;
    }

    // Обновление очереди
    if (item.isQueue()) {
      const findQueue = findTreeItem(sidebarItems.value, (_item) => {
        return _item.isQueue() && item.Id === _item.Id;
      }) as QueueTasksModel | undefined;

      if (findQueue) Object.assign(findQueue, { ...findQueue, Name: name });

      if (findQueue.ParentGroupId) {
        sortSubItems(findQueue.ParentGroupId, TreeItemType.Group);
      } else if (findQueue.NamespaceId) {
        sortSubItems(findQueue.NamespaceId, TreeItemType.Namespace);
      }
      return;
    }
  };
  /**Сортировка групп и очередей родительского объекта */
  const sortSubItems = (parentId: number | string, type: TreeItemType) => {
    let parent: NamespaceModel | GroupModel;

    switch (type) {
      case TreeItemType.Namespace:
        parent = findTreeItem(sidebarItems.value, (_item) => {
          return _item.isNamespace() && parentId === _item.Id;
        }) as NamespaceModel | undefined;
        break;
      case TreeItemType.Group:
        parent = findTreeItem(sidebarItems.value, (_item) => {
          return _item.isGroup() && parentId === _item.Id;
        }) as GroupModel | undefined;
        break;
    }

    if (parent) {
      sortByName(parent.Groups);
      sortByName(parent.QueueTasks);
    }
  };
  /**
   * Перемещение элемента в другую директорию
   * @param item Элемент
   * @param toId Id места вставки
   */
  const move = (item: GroupModel | QueueTasksModel, toId: string | number) => {
    if (item.isGroup()) {
      item = new GroupModel({ ...item });
    } else if (item.isQueue()) {
      item = new QueueTasksModel({ ...item });
    }

    item.ParentGroupId = undefined;
    item.NamespaceId = undefined;
    if (typeof toId == 'number') {
      item.ParentGroupId = toId;
    } else if (typeof toId == 'string') {
      item.NamespaceId = toId;
    }

    removeItem(item.Id, item.Type);
    addMenuItem(item);
  };

  return {
    sidebarItems: computed(() => sidebarItems.value),
    setSidebarItems,
    openMenuItemFromRoute,
    removeItem,
    addMenuItem,
    updateItem,
    move,
  };
};
